<script setup>
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import { usePageLoadingStore } from "../../../../../stores/pageLoading";
import adminJobfairStateService from "../../../services/jobfair/adminJobfairStateService";
import mypageJobFairResumeService from "../../../../user/services/mypage/jobFair/mypageJobFairResumeService";
import MemberJobFairResumeApplicantListView from "./MemberJobFairResumeApplicantListView.vue";
import JobFairResumeDetailModal from "../../../../user/components/modal/jobFair/JobFairResumeDetailModal.vue";

const { $pageListBoardNo, $toast, $number, $gbNameKR, $yyyymmddHHmmss, alert, confirm } = getCurrentInstance().appContext.config.globalProperties;

const route = useRoute();
const router = useRouter();
const loadingStore = usePageLoadingStore();

const props = defineProps({
  userId: {
    type: [String, Number],
    required: true,
  }
});

const currentPage = ref(1);
const pageSize = ref(10);
const totalPages = ref(1);
const totalCount = ref(0);
const list = ref([]);

const isApplicantView = ref(false);
const selectedResumeId = ref('');
const selectedFileYn = ref('');
const jobFairResumeModal = ref(null);

const pageSizeGroup = ref([
  { label:"10개씩 보기", value: 10 },
  { label:"30개씩 보기", value: 30 },
  { label:"50개씩 보기", value: 50 },
  { label:"100개씩 보기", value: 100 },
])

const onSelectPage = (pageNo) => {
  currentPage.value = pageNo;
  retrieveList();
}

// 페이지당 개수 변경
const onSelectPageSize = (option) => {
  pageSize.value = option.value;
  currentPage.value = 1;
  retrieveList();
}

const retrieveList = () => {
  let paramObj = {
    userId: props.userId,
    pageNumber: currentPage.value,
    size: pageSize.value,
  }
  loadingStore.loading = true;
  adminJobfairStateService.retrieveJobFairResumeListAdminPagingByUserId(paramObj)
    .then( async (res) => {
      loadingStore.loading = false;
      if (res.data.code.toLowerCase() === "success") {
        const result = res.data.result;
        totalPages.value = result.totalPages;
        totalCount.value = result.totalElements;
        list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
      }
    })
    .catch((res) => {
      loadingStore.loading = false;
      $toast("error", res);
    });
};

// 이력서 보기
const onClickMyResume = async (resumeId, resOpenYn, applicantCnt, intvProposalCnt) => {
  await jobFairResumeModal.value.show({
    resumeId,
    showDeleteBtn: applicantCnt === 0 && intvProposalCnt === 0, // 받은 면접제안 상태로 변경해야함.
  });
}

// 입사지원 수 클릭
const onClickApplicantCnt = (resumeId, fileYn) => {
  isApplicantView.value = true;
  selectedResumeId.value = resumeId;
  selectedFileYn.value = fileYn;
}

// 인재정보 공개여부 변경
const onClickChangeResOpenYn = async (resume, resOpenYn) => {
  // 기존에 오픈된 이력서 존재 여부 확인. true/false
  let resumeY = 'N';
  if(resOpenYn === 'Y') {
    const result = await mypageJobFairResumeService.retrieveResumeResOpenYn(props.userId);
    resumeY = result.data.result || 'N';
  }

  let ok = null;
  if(resumeY === 'Y') {
    ok = await confirm([
      "상시채용관 인재정보에 공개할 수 있는",
      "<span style='color:red;'>이력서는 1개만 가능</span>합니다.",
      "<br/>",
      "해당 이력서를 공개 하시겠습니까?",
      "<span style='color:red;'>나머지는 비공개 처리 됩니다.</span>"
    ], "인재정보 공개");
    if(!ok) {
      return;
    }
  }

  let resOpenYnName = resOpenYn === 'Y' ? '공개' : '비공개';

  if(ok === null) {
    ok = await confirm([`해당 이력서를 ${resOpenYnName} 하시겠습니까?`], "확인");
  }

  if (ok) {
    const paramObj = {
      resumeId: resume.resumeId,
      userId: props.userId,
      resOpenYn,
    };

    mypageJobFairResumeService.updateStatusResumeResOpenYn(paramObj)
      .then(async (res) => {
        loadingStore.loading = false;
        if (res.data.code.toLowerCase() === 'success') {
          alert(["선택한 이력서가", `상시채용관 인재정보에 ${resOpenYnName} 되었습니다.`]);
          retrieveList();
        } else {
          alert(["인재정보 공개 여부 변경이 정상적으로 이루어지지 않았습니다."]);
        }
      })
      .catch((error) => {
        loadingStore.loading = false;
        alert(["인재정보 공개 여부 변경이 정상적으로 이루어지지 않았습니다."]);
      });
  }
}

onMounted(()=>{
  retrieveList();
});
</script>

<template>
  <div v-if="!isApplicantView">
    <div class="list-top">
      <p>전체 <span>{{ $number(totalCount)}}</span>개</p>
      <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize"/>
    </div>
    <!-- e: 버튼영역 -->
    <div class="table-form">
      <table class="horizontal" v-if="list !== null">
        <tr>
          <th>NO</th>
          <th>이력서 제목</th>
          <th>입사지원</th>
          <th>최근 수정일</th>
          <th>등록일</th>
          <th>인재정보 공개</th>
        </tr>
        <tr v-for="resume in list">
          <td>{{ resume.boardNo }}</td>
          <td class="text-overflow long-overflow">
            <span class="file blue" v-if="resume.fileYn === 'Y'" v-for="resumeAttaches in resume.resumeAttaches" @click="$downloadForPrivate({fileName:resumeAttaches.fileName, fileDir: resumeAttaches.fileDir })">
                {{ resumeAttaches.fileName }}
            </span>
            <span class="clickable blue" v-else @click="onClickMyResume(resume.resumeId)">
                {{ resume.title }}
            </span>
          </td>
          <td class="clickable blue" @click="onClickApplicantCnt(resume.resumeId, resume.fileYn)">{{ resume.applicantCnt }}</td>
          <td>
            <span v-if="resume.fileYn === 'Y'">-</span>
            <span v-else>{{ $yyyymmddHHmmss(resume.modifyDate) }}</span>
          </td>
          <td>{{ $yyyymmddHHmmss(resume.createDate) }}</td>
          <td>
            <span v-if="resume.resOpenYn === 'Y'">
                <img src="../../../../../assets/images/toggle_on.png" @click="onClickChangeResOpenYn(resume, 'N')" style="cursor:pointer;" />
            </span>
            <span v-else>
                <span v-if="resume.fileYn === 'Y'">
                    -
                </span>
                <span v-else>
                    <img src="../../../../../assets/images/toggle_off.png" @click="onClickChangeResOpenYn(resume, 'Y')" style="cursor:pointer;" />
                </span>
            </span>
          </td>
        </tr>
      </table>
      <div class="result-none" v-if="list.length === 0">
        등록된 이력서가 없습니다.
      </div>
    </div>
    <Paginate
      v-if="list.length > 0"
      v-model="currentPage"
      :page-count="totalPages"
      :click-handler="onSelectPage"
    />
  </div>
  <div v-if="isApplicantView">
    <MemberJobFairResumeApplicantListView
      :resume-id="selectedResumeId"
      :file-yn="selectedFileYn"
      :user-id="props.userId"
      @go-back="isApplicantView = false"
    />
  </div>
  <JobFairResumeDetailModal ref="jobFairResumeModal" /> <!-- 특별채용관 이력서 상세 -->
</template>
<style scoped>
</style>
